import { time } from 'console';
import { useEffect, useRef } from 'react';

import styles from './AnimatedSensor.module.css';

interface AnimatedSensorProps {
  className?: string;
  animation: 'syncing' | 'powering-on';
}

export const AnimatedSensor = ({ className, animation }: AnimatedSensorProps) => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const topRef = useRef<SVGGElement>(null);
  const oneFromTopRef = useRef<SVGGElement>(null);
  const oneFromBottomRef = useRef<SVGGElement>(null);
  const bottomRef = useRef<SVGGElement>(null);

  function safeFadeOut(element: SVGGElement | null) {
    if (element) {
      element.style.opacity = '0';
    }
  }

  function safeFadeIn(element: SVGGElement | null) {
    if (element) {
      element.style.opacity = '1';
    }
  }

  useEffect(() => {
    function stepOne() {
      safeFadeIn(bottomRef.current);
      safeFadeOut(oneFromBottomRef.current);
      safeFadeOut(oneFromTopRef.current);
      safeFadeOut(topRef.current);
    }

    function stepTwo() {
      safeFadeIn(oneFromBottomRef.current);
      safeFadeOut(bottomRef.current);
      safeFadeOut(oneFromTopRef.current);
      safeFadeOut(topRef.current);
    }

    function stepThree() {
      safeFadeIn(oneFromTopRef.current);
      safeFadeOut(oneFromBottomRef.current);
      safeFadeOut(bottomRef.current);
      safeFadeOut(topRef.current);
    }

    function stepFour() {
      safeFadeIn(topRef.current);
      safeFadeOut(oneFromTopRef.current);
      safeFadeOut(oneFromBottomRef.current);
      safeFadeOut(bottomRef.current);
    }

    function stepFive() {
      safeFadeOut(topRef.current);
      safeFadeOut(oneFromTopRef.current);
      safeFadeOut(oneFromBottomRef.current);
      safeFadeOut(bottomRef.current);
    }

    function playAnimation() {
      const animationDuration = 225;
      requestAnimationFrame(() => {
        timeoutRef.current = setTimeout(() => {
          stepOne();

          timeoutRef.current = setTimeout(() => {
            stepTwo();

            timeoutRef.current = setTimeout(() => {
              stepThree();

              timeoutRef.current = setTimeout(() => {
                stepFour();

                timeoutRef.current = setTimeout(() => {
                  stepFive();

                  timeoutRef.current = setTimeout(() => {
                    playAnimation();
                  }, 600);
                }, animationDuration);
              }, animationDuration);
            }, animationDuration);
          }, animationDuration);
        }, animationDuration);
      });
    }

    playAnimation();

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [animation]);

  return (
    <svg fill='none' height='71' viewBox='0 0 47 71' width='47' xmlns='http://www.w3.org/2000/svg'>
      <g filter='url(#filter0_i_8202_26)'>
        <rect fill='#667A7A' height='70.5921' rx='10.525' width='46.25' x='0.25' />
      </g>
      <g filter='url(#filter1_i_8202_26)'>
        <path
          d='M26.5958 43.1C30.6642 41.7508 33.5989 37.9151 33.5989 33.3939C33.5989 27.7476 29.0216 23.1703 23.3752 23.1703C17.7289 23.1703 13.1516 27.7476 13.1516 33.3939C13.1515 37.9152 16.0864 41.751 20.1549 43.1001L20.1549 59.6031C20.1549 61.3817 21.5968 62.8236 23.3754 62.8236C25.154 62.8236 26.5958 61.3817 26.5958 59.6031L26.5958 43.1Z'
          fill='#829696'
        />
      </g>
      {/* Top circle */}
      <g className={styles.dot} filter='url(#filter2_d_8202_26)' ref={topRef}>
        <circle cx='23.375' cy='46.875' fill='url(#paint0_radial_8202_26)' r='1.875' />
      </g>

      {/* 1 from top circle */}
      <g className={styles.dot} filter='url(#filter3_d_8202_26)' ref={oneFromTopRef}>
        <circle cx='23.375' cy='51' fill='url(#paint1_radial_8202_26)' r='1.75' />
      </g>

      {/* 1 from bottom circle */}
      <g className={styles.dot} filter='url(#filter4_d_8202_26)' ref={oneFromBottomRef}>
        <circle cx='23.375' cy='54.875' fill='url(#paint2_radial_8202_26)' r='1.625' />
      </g>

      {/* Bottom circle */}
      <g className={styles.dot} filter='url(#filter5_d_8202_26)' ref={bottomRef}>
        <circle cx='23.375' cy='58.5' fill='url(#paint3_radial_8202_26)' r='1.5' />
      </g>
      <path
        d='M23.25 38.8749C20.562 38.8749 18.375 36.6842 18.375 33.9917C18.375 32.528 19.0137 31.1558 20.1272 30.2288C20.1792 30.1853 20.2392 30.1524 20.304 30.1321C20.3687 30.1118 20.4367 30.1045 20.5043 30.1106C20.5718 30.1166 20.6375 30.1359 20.6976 30.1674C20.7577 30.1989 20.8109 30.2419 20.8543 30.294C20.8978 30.3461 20.9305 30.4062 20.9506 30.471C20.9708 30.5358 20.9779 30.6039 20.9717 30.6714C20.9655 30.7389 20.946 30.8046 20.9144 30.8645C20.8828 30.9245 20.8396 30.9777 20.7874 31.021C19.9097 31.7523 19.4062 32.8351 19.4062 33.9917C19.4062 36.1156 21.1305 37.8437 23.25 37.8437C25.3695 37.8437 27.0938 36.1156 27.0938 33.9917C27.0937 33.4241 26.9679 32.8635 26.7255 32.3503C26.483 31.8371 26.1299 31.3839 25.6915 31.0233C25.6372 30.9809 25.5919 30.9281 25.5584 30.8679C25.5248 30.8077 25.5037 30.7415 25.4962 30.673C25.4887 30.6045 25.495 30.5352 25.5148 30.4692C25.5345 30.4032 25.5673 30.3418 25.6111 30.2887C25.655 30.2355 25.7091 30.1917 25.7701 30.1598C25.8312 30.128 25.898 30.1086 25.9667 30.103C26.0354 30.0974 26.1045 30.1056 26.1699 30.1271C26.2354 30.1486 26.2958 30.1831 26.3477 30.2283C26.9035 30.6855 27.3511 31.26 27.6584 31.9107C27.9657 32.5614 28.1251 33.2721 28.125 33.9917C28.125 36.6842 25.938 38.8749 23.25 38.8749Z'
        fill='black'
        fillOpacity='0.1'
      />
      <path
        d='M23.25 34.375C23.1132 34.375 22.9821 34.3207 22.8854 34.224C22.7887 34.1273 22.7344 33.9961 22.7344 33.8594V29.6406C22.7344 29.5039 22.7887 29.3727 22.8854 29.276C22.9821 29.1793 23.1132 29.125 23.25 29.125C23.3868 29.125 23.5179 29.1793 23.6146 29.276C23.7113 29.3727 23.7656 29.5039 23.7656 29.6406V33.8594C23.7656 33.9961 23.7113 34.1273 23.6146 34.224C23.5179 34.3207 23.3868 34.375 23.25 34.375Z'
        fill='black'
        fillOpacity='0.1'
      />
      <defs>
        <filter
          colorInterpolationFilters='sRGB'
          filterUnits='userSpaceOnUse'
          height='70.5921'
          id='filter0_i_8202_26'
          width='46.25'
          x='0.25'
          y='0'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend in='SourceGraphic' in2='BackgroundImageFix' mode='normal' result='shape' />
          <feColorMatrix
            in='SourceAlpha'
            result='hardAlpha'
            type='matrix'
            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          />
          <feOffset />
          <feGaussianBlur stdDeviation='1' />
          <feComposite in2='hardAlpha' k2='-1' k3='1' operator='arithmetic' />
          <feColorMatrix type='matrix' values='0 0 0 0 0.929412 0 0 0 0 0.94902 0 0 0 0 0.94902 0 0 0 0.6 0' />
          <feBlend in2='shape' mode='normal' result='effect1_innerShadow_8202_26' />
        </filter>
        <filter
          colorInterpolationFilters='sRGB'
          filterUnits='userSpaceOnUse'
          height='41.6534'
          id='filter1_i_8202_26'
          width='22.4474'
          x='11.1516'
          y='23.1703'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feBlend in='SourceGraphic' in2='BackgroundImageFix' mode='normal' result='shape' />
          <feColorMatrix
            in='SourceAlpha'
            result='hardAlpha'
            type='matrix'
            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          />
          <feOffset dx='-2' dy='2' />
          <feGaussianBlur stdDeviation='5.45' />
          <feComposite in2='hardAlpha' k2='-1' k3='1' operator='arithmetic' />
          <feColorMatrix type='matrix' values='0 0 0 0 0.4 0 0 0 0 0.478431 0 0 0 0 0.478431 0 0 0 1 0' />
          <feBlend in2='shape' mode='normal' result='effect1_innerShadow_8202_26' />
        </filter>
        <filter
          colorInterpolationFilters='sRGB'
          filterUnits='userSpaceOnUse'
          height='11.75'
          id='filter2_d_8202_26'
          width='11.75'
          x='17.5'
          y='41'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feColorMatrix
            in='SourceAlpha'
            result='hardAlpha'
            type='matrix'
            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          />
          <feOffset />
          <feGaussianBlur stdDeviation='2' />
          <feComposite in2='hardAlpha' operator='out' />
          <feColorMatrix type='matrix' values='0 0 0 0 0.376471 0 0 0 0 0.909804 0 0 0 0 0.901961 0 0 0 1 0' />
          <feBlend in2='BackgroundImageFix' mode='normal' result='effect1_dropShadow_8202_26' />
          <feBlend in='SourceGraphic' in2='effect1_dropShadow_8202_26' mode='normal' result='shape' />
        </filter>
        <filter
          colorInterpolationFilters='sRGB'
          filterUnits='userSpaceOnUse'
          height='10.9667'
          id='filter3_d_8202_26'
          width='10.9667'
          x='17.8917'
          y='45.5167'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feColorMatrix
            in='SourceAlpha'
            result='hardAlpha'
            type='matrix'
            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          />
          <feOffset />
          <feGaussianBlur stdDeviation='1.86667' />
          <feComposite in2='hardAlpha' operator='out' />
          <feColorMatrix type='matrix' values='0 0 0 0 0.376471 0 0 0 0 0.909804 0 0 0 0 0.901961 0 0 0 1 0' />
          <feBlend in2='BackgroundImageFix' mode='normal' result='effect1_dropShadow_8202_26' />
          <feBlend in='SourceGraphic' in2='effect1_dropShadow_8202_26' mode='normal' result='shape' />
        </filter>
        <filter
          colorInterpolationFilters='sRGB'
          filterUnits='userSpaceOnUse'
          height='10.1833'
          id='filter4_d_8202_26'
          width='10.1833'
          x='18.2833'
          y='49.7833'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feColorMatrix
            in='SourceAlpha'
            result='hardAlpha'
            type='matrix'
            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          />
          <feOffset />
          <feGaussianBlur stdDeviation='1.73333' />
          <feComposite in2='hardAlpha' operator='out' />
          <feColorMatrix type='matrix' values='0 0 0 0 0.376471 0 0 0 0 0.909804 0 0 0 0 0.901961 0 0 0 1 0' />
          <feBlend in2='BackgroundImageFix' mode='normal' result='effect1_dropShadow_8202_26' />
          <feBlend in='SourceGraphic' in2='effect1_dropShadow_8202_26' mode='normal' result='shape' />
        </filter>
        <filter
          colorInterpolationFilters='sRGB'
          filterUnits='userSpaceOnUse'
          height='9.4'
          id='filter5_d_8202_26'
          width='9.4'
          x='18.675'
          y='53.8'>
          <feFlood floodOpacity='0' result='BackgroundImageFix' />
          <feColorMatrix
            in='SourceAlpha'
            result='hardAlpha'
            type='matrix'
            values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'
          />
          <feOffset />
          <feGaussianBlur stdDeviation='1.6' />
          <feComposite in2='hardAlpha' operator='out' />
          <feColorMatrix type='matrix' values='0 0 0 0 0.376471 0 0 0 0 0.909804 0 0 0 0 0.901961 0 0 0 1 0' />
          <feBlend in2='BackgroundImageFix' mode='normal' result='effect1_dropShadow_8202_26' />
          <feBlend in='SourceGraphic' in2='effect1_dropShadow_8202_26' mode='normal' result='shape' />
        </filter>
        <radialGradient
          cx='0'
          cy='0'
          gradientTransform='translate(23.75 46.5) rotate(116.565) scale(2.23607)'
          gradientUnits='userSpaceOnUse'
          id='paint0_radial_8202_26'
          r='1'>
          <stop stopColor='#9BEDEC' />
          <stop offset='1' stopColor='#85CFCE' />
        </radialGradient>
        <radialGradient
          cx='0'
          cy='0'
          gradientTransform='translate(23.725 50.65) rotate(116.565) scale(2.087)'
          gradientUnits='userSpaceOnUse'
          id='paint1_radial_8202_26'
          r='1'>
          <stop stopColor='#9BEDEC' />
          <stop offset='1' stopColor='#85CFCE' />
        </radialGradient>
        <radialGradient
          cx='0'
          cy='0'
          gradientTransform='translate(23.7 54.55) rotate(116.565) scale(1.93793)'
          gradientUnits='userSpaceOnUse'
          id='paint2_radial_8202_26'
          r='1'>
          <stop stopColor='#9BEDEC' />
          <stop offset='1' stopColor='#85CFCE' />
        </radialGradient>
        <radialGradient
          cx='0'
          cy='0'
          gradientTransform='translate(23.675 58.2) rotate(116.565) scale(1.78885)'
          gradientUnits='userSpaceOnUse'
          id='paint3_radial_8202_26'
          r='1'>
          <stop stopColor='#9BEDEC' />
          <stop offset='1' stopColor='#85CFCE' />
        </radialGradient>
      </defs>
    </svg>
  );
};
